<template>
  <div>
    <RecommendationAlgoliaBundle
      v-if="selectedOffer && selectedOffer.offerLegacyId && selectedOffer.price"
      v-show="!hideBundleAndCrossSell"
      class="mb-72 overflow-hidden"
      :main-product="{
        title: product.titles.default,
        description: product.subtitleElements.join(' - '),
        image: product.images?.[0]?.url,
        productPageLink: {
          type: 'internal',
          name: PRODUCT.HOME,
          params: routeParams as Record<string, string>,
          query: {},
          hash: {},
        },
        priceNew: product.priceWhenNew,
        productId: product.productId,
        brand: product.brand,
        category: product.tracking.categoryName,
        id: product.productId,
        model: product.model ?? '',
        specifications: {},
        mobilePlanOfferId: mobilePlan,
        partnerPromoCodes: product.includedServiceOffers.partnerPromoCodes,
        grade: selectedOffer.grade,
        specialOfferType: selectedOffer.offerType.value,
        price: selectedOffer.price,
        listingPublicId: selectedOffer?.offerId,
        listingId: `${selectedOffer?.offerLegacyId}`,
        sellerId: selectedOffer.merchant?.merchantId,
      }"
      :title="i18n(translations.bundleTitle)"
      @add-to-cart="onBundleAddToCart"
    />

    <RecommendationAlgoliaCarousel
      block-zone="pdp_similar_items"
      class="mb-72"
      :recommendation-query="{
        objectIds: algoliaObjectIds,
        model: 'related-products',
        options: {
          maxRecommendations: 12,
          ...(product.tracking.categoryId && {
            fallbackParameters: {
              filters: `('cat_id':'${product.tracking.categoryId}')`,
            },
          }),
        },
      }"
      :title="
        withRanking
          ? i18n(translations.productRanking)
          : i18n(translations.moreTitle)
      "
      :trackingData="{
        list: 'product page cousins 1',
        zone: 'pdp_similar_items',
      }"
      :with-add-to-cart="false"
      @error="onRecommendationsError"
      @loaded="onRecommendationsLoaded"
    >
      <template v-if="withRanking" #post-card="{ index }">
        <RankingTag :index />
      </template>
    </RecommendationAlgoliaCarousel>

    <RecommendationAlgoliaCarousel
      v-show="!hideBundleAndCrossSell"
      block-zone="pdp_pairs_well_with"
      class="mb-72"
      :recommendation-query="{
        objectIds: algoliaObjectIds,
        model: 'bought-together',
        options: {
          maxRecommendations: 12,
          queryParameters: {
            ruleContexts: RECOMMENDATION_RULE_CONTEXTS.CROSS_SELL,
          },
        },
      }"
      :title="i18n(translations.crossTitle)"
      :trackingData="{
        list: 'product page cousins 2',
        zone: 'pdp_pairs_well_with',
      }"
      :with-add-to-cart="false"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import RecommendationAlgoliaCarousel from '@backmarket/nuxt-layer-recommendation/RecommendationAlgoliaCarousel.vue'
import { RECOMMENDATION_RULE_CONTEXTS } from '@backmarket/nuxt-layer-recommendation/constants/recommendationRuleContexts'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import { PRODUCT } from '~/scopes/product-core/route-names'
import RecommendationAlgoliaBundle from '~/scopes/recommendation/components/RecommendationAlgoliaBundle.vue'

import translations from './Recommendations.translations'
import RankingTag from './components/RankingTag/RankingTag.vue'
import { useProductUrlToAlgoliaObjectId } from './composables/useProductUrlToAlgoliaObjectId'

const { selectedOffer, product, withRanking, hideBundleAndCrossSell } =
  defineProps<{
    product: GetProductResponse
    selectedOffer: GetPickersResponse['selectedOffer']
    withRanking?: boolean
    hideBundleAndCrossSell?: boolean
  }>()

const emit = defineEmits<{
  'bundle-add-to-cart': [Array<string>, Product | null]
  'recommendations-loaded': [Array<Product>]
  'recommendations-error': []
}>()

const i18n = useI18n()
const { productId, mobilePlan, slug } = useUrlParams()
const routeParams = { uuid: productId, slugV2: slug }
const algoliaObjectId = useProductUrlToAlgoliaObjectId()

const algoliaObjectIds = computed(() =>
  algoliaObjectId.value ? [algoliaObjectId.value] : null,
)

function onBundleAddToCart(
  listings: Array<string>,
  bundledProduct: Product | null,
) {
  emit('bundle-add-to-cart', listings, bundledProduct)
}

function onRecommendationsLoaded(products: Array<Product>) {
  emit('recommendations-loaded', products)
}

function onRecommendationsError() {
  emit('recommendations-error')
}
</script>
